import type { KeyboardEventHandler, ReactNode } from 'react';
import { Input } from '@/components/cms/ui/input';
import { Card, CardHeading } from '@/components/ui/card';
import LureoButton from '@/common/LureoButton';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { ImageButton } from '@/components/ui/image-button';
import { FieldLabel } from '@/components/ui/text';

export interface ProfileValues {
  full_name: string;
  org_name: string;
  email: string;
  new_email: string;
  current_password: string;
}

interface ProfileCardProps {
  /** The profile photo: the user's avatar, at h-24. */
  photo: ReactNode;
  onChangePhoto: () => void;
  values: ProfileValues;
  onChange: (field: keyof ProfileValues, value: string) => void;
  /** Whether the user owns the org: only an owner can rename it. */
  owner: boolean;
  /** Enter in a field: saves, or from the new email moves on to the password. */
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  onNewEmailKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  saving: boolean;
  /** Whether Save Changes can be used: something changed, and a new email has its password. */
  canSave: boolean;
  onSave: () => void;
  /** The names cannot change until the membership they belong to has loaded. */
  namesDisabled?: boolean;
  /** Under the password an email change needs: the way to set one for an account without. */
  passwordPrompt?: ReactNode;
}

/** My Account's Profile tab: the photo, the names and the email, and Save Changes. */
export function ProfileCard({ photo, onChangePhoto, values, onChange, owner, onKeyDown, onNewEmailKeyDown, saving, canSave, onSave, namesDisabled, passwordPrompt }: ProfileCardProps) {
  return (
    <div className="space-y-6">
      <Card elevation="high">
        <CardHeading title="Profile Information" description="Update your personal information and profile photo" className="mb-6" />
        <div className="space-y-6">
          <div className="flex items-center gap-6">
            <div className="relative">
              <FieldLabel variant="plain" mb>Profile Photo</FieldLabel>
              {/* No line round the photo: it is never picked or invalid, so it sits flush under its label */}
              <ImageButton round label="Change profile photo" onClick={onChangePhoto} className="border-0">
                {photo}
              </ImageButton>
            </div>
          </div>

          {/* Each field's label is only as tall as its text, close on its field */}
          <div className="grid gap-4">
            <div className="grid gap-2">
              <FieldLabel variant="plain" htmlFor="full_name" className="leading-none">Full Name</FieldLabel>
              <Input
                id="full_name"
                value={values.full_name}
                disabled={namesDisabled}
                onChange={(e) => onChange('full_name', e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Enter your full name"
              />
            </div>
            {owner ? (
              <div className="grid gap-2">
                <FieldLabel variant="plain" htmlFor="org_name" className="leading-none">Organization Name</FieldLabel>
                <Input
                  id="org_name"
                  value={values.org_name}
                  disabled={namesDisabled}
                  onChange={(e) => onChange('org_name', e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="Enter your organization name"
                />
              </div>
            ) : (
              <div className="grid gap-2">
                <FieldLabel variant="plain" htmlFor="org_name" className="leading-none">Organization Name</FieldLabel>
                <Input
                  id="org_name"
                  value={values.org_name}
                  disabled
                  className="bg-lureo-neutral/50 cursor-not-allowed"
                  placeholder="Organization name"
                />
              </div>
            )}
            <div className="grid gap-2">
              <FieldLabel variant="plain" htmlFor="email" className="leading-none">Current Email Address</FieldLabel>
              <Input
                id="email"
                type="email"
                value={values.email}
                disabled
                className="bg-lureo-neutral/50 cursor-not-allowed"
                placeholder="Current email address"
              />
            </div>
            <div className="grid gap-2 mt-2">
              <FieldLabel variant="plain" htmlFor="new_email" className="leading-none">New Email Address (Optional)</FieldLabel>
              <Input
                id="new_email"
                type="email"
                value={values.new_email}
                onChange={(e) => onChange('new_email', e.target.value)}
                onKeyDown={onNewEmailKeyDown}
                placeholder="Enter new email address"
              />
            </div>
            {values.new_email && values.new_email !== values.email && (
              <div className="grid gap-2">
                <FieldLabel variant="plain" htmlFor="profile_current_password" className="leading-none">Current Password (Required for email change)</FieldLabel>
                <Input
                  id="profile_current_password"
                  type="password"
                  value={values.current_password}
                  onChange={(e) => onChange('current_password', e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="Enter your current password"
                />
                {passwordPrompt}
              </div>
            )}
          </div>

          <LureoButton
            onClick={onSave}
            variant="secondary"
            className="h-9 rounded-lureo-radius-button-pill px-6"
            disabled={saving || !canSave}>
            {saving && <LoadingSpinner size="sm" className="mr-2" />}
            Save Changes
          </LureoButton>
        </div>
      </Card>
    </div>
  );
}
